/**
 * BaxterDog partner marketing attribution capture.
 * Persists first-touch and latest-touch partner/UTM params to storage and cart attributes.
 * Sends lightweight visit beacons to the partner portal on tracked landings.
 */
(() => {
  "use strict";

  const VISIT_ENDPOINT = "https://partners.baxterdog.com/api/track/visit";

  const STORAGE_KEYS = {
    first: "bdm_partner_attribution_first",
    latest: "bdm_partner_attribution_latest",
  };

  const SESSION_KEYS = {
    cartSynced: "bdm_partner_attribution_cart_synced",
    cartHash: "bdm_partner_attribution_cart_hash",
    visitHash: "bdm_partner_attribution_visit_hash",
    visitSessionKey: "bdm_partner_attribution_session_key",
  };

  const VISIT_HASH_FIELDS = [
    "ref",
    "bdm_asset",
    "bdm_channel",
    "bdm_campaign",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "landing_path",
    "session_key",
  ];

  /** Legacy cart attribute kept for partner portal webhook compatibility. */
  const LEGACY_PARTNER_REF_KEY = "baxterdog_partner_ref";

  const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

  const TRACKING_PARAMS = [
    "ref",
    "bdm_asset",
    "bdm_channel",
    "bdm_campaign",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
  ];

  const MEANINGFUL_PARAMS = ["ref", "bdm_asset", "bdm_campaign", "utm_campaign"];

  const REF_SLUG_PATTERN = /^[a-z0-9-]{3,64}$/;
  const PARAM_VALUE_PATTERN = /^[a-zA-Z0-9._%-]{1,128}$/;

  function isTopWindow() {
    try {
      return window.self === window.top;
    } catch (_error) {
      return false;
    }
  }

  function warn(message, error) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[bdm-partner-attribution]", message, error || "");
    }
  }

  function sanitizeRef(value) {
    if (typeof value !== "string") return null;
    const trimmed = value.trim().toLowerCase();
    return REF_SLUG_PATTERN.test(trimmed) ? trimmed : null;
  }

  function sanitizeParam(value) {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    if (!trimmed || !PARAM_VALUE_PATTERN.test(trimmed)) return null;
    return trimmed;
  }

  function readSearchParams() {
    return new URLSearchParams(window.location.search);
  }

  function hasMeaningfulTrackingParams(searchParams) {
    return MEANINGFUL_PARAMS.some((key) => {
      const raw = searchParams.get(key);
      if (!raw || !raw.trim()) return false;
      if (key === "ref") return Boolean(sanitizeRef(raw));
      return Boolean(sanitizeParam(raw));
    });
  }

  function readTrackingFromSearchParams(searchParams) {
    const tracking = {};

    TRACKING_PARAMS.forEach((key) => {
      const raw = searchParams.get(key);
      if (!raw) return;

      if (key === "ref") {
        const ref = sanitizeRef(raw);
        if (ref) tracking.ref = ref;
        return;
      }

      const value = sanitizeParam(raw);
      if (value) tracking[key] = value;
    });

    return tracking;
  }

  function readPageContext() {
    const { origin, pathname, href } = window.location;
    const landingPath = pathname || "/";

    return {
      landing_page: `${origin}${landingPath}`,
      landing_path: landingPath,
      landing_url: href,
      referrer: document.referrer || "",
    };
  }

  function buildAttributionSnapshot(searchParams) {
    const now = new Date().toISOString();
    const tracking = readTrackingFromSearchParams(searchParams);
    const context = readPageContext();

    return {
      ...tracking,
      ...context,
      first_seen_at: now,
      last_seen_at: now,
    };
  }

  function parseJson(value) {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  }

  function readLocalStorage(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  function writeLocalStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (_error) {
      // Privacy settings may block storage; cookie fallback continues.
    }
  }

  function readCookie(key) {
    const prefix = `${encodeURIComponent(key)}=`;
    const match = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix));

    if (!match) return null;
    return decodeURIComponent(match.slice(prefix.length));
  }

  function writeCookie(key, value) {
    const encodedKey = encodeURIComponent(key);
    const encodedValue = encodeURIComponent(value);
    document.cookie = `${encodedKey}=${encodedValue}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax; Secure`;
  }

  function readStoredAttribution(key) {
    const fromLocal = parseJson(readLocalStorage(key));
    if (fromLocal && typeof fromLocal === "object") return fromLocal;

    const fromCookie = parseJson(readCookie(key));
    if (fromCookie && typeof fromCookie === "object") return fromCookie;

    return null;
  }

  function persistAttribution(key, attribution) {
    const serialized = JSON.stringify(attribution);
    writeLocalStorage(key, serialized);
    writeCookie(key, serialized);
  }

  function setStringAttribute(attributes, name, value) {
    if (typeof value !== "string" || !value) return;
    attributes[name] = value;
  }

  function buildCartAttributes(firstTouch, latestTouch) {
    const attributes = {};

    if (latestTouch) {
      setStringAttribute(attributes, "bdm_ref", latestTouch.ref);
      setStringAttribute(attributes, LEGACY_PARTNER_REF_KEY, latestTouch.ref);
      setStringAttribute(attributes, "bdm_asset", latestTouch.bdm_asset);
      setStringAttribute(attributes, "bdm_channel", latestTouch.bdm_channel);
      setStringAttribute(attributes, "bdm_campaign", latestTouch.bdm_campaign);
      setStringAttribute(attributes, "bdm_landing_page", latestTouch.landing_page);
      setStringAttribute(attributes, "bdm_landing_path", latestTouch.landing_path);
      setStringAttribute(attributes, "bdm_referrer", latestTouch.referrer);
      setStringAttribute(attributes, "bdm_first_seen_at", latestTouch.first_seen_at);
      setStringAttribute(attributes, "bdm_last_seen_at", latestTouch.last_seen_at);
      setStringAttribute(attributes, "utm_source", latestTouch.utm_source);
      setStringAttribute(attributes, "utm_medium", latestTouch.utm_medium);
      setStringAttribute(attributes, "utm_campaign", latestTouch.utm_campaign);
      setStringAttribute(attributes, "utm_content", latestTouch.utm_content);
    }

    if (firstTouch) {
      setStringAttribute(attributes, "bdm_first_ref", firstTouch.ref);
      setStringAttribute(attributes, "bdm_first_asset", firstTouch.bdm_asset);
      setStringAttribute(attributes, "bdm_first_channel", firstTouch.bdm_channel);
      setStringAttribute(attributes, "bdm_first_campaign", firstTouch.bdm_campaign);
      setStringAttribute(attributes, "bdm_first_landing_page", firstTouch.landing_page);
      setStringAttribute(attributes, "bdm_first_seen_at", firstTouch.first_seen_at);
      setStringAttribute(attributes, "bdm_first_utm_source", firstTouch.utm_source);
      setStringAttribute(attributes, "bdm_first_utm_medium", firstTouch.utm_medium);
      setStringAttribute(attributes, "bdm_first_utm_campaign", firstTouch.utm_campaign);
      setStringAttribute(attributes, "bdm_first_utm_content", firstTouch.utm_content);
    }

    return attributes;
  }

  function hashAttributes(attributes) {
    const keys = Object.keys(attributes).sort();
    const normalized = {};
    keys.forEach((key) => {
      normalized[key] = attributes[key];
    });
    return JSON.stringify(normalized);
  }

  function readSessionItem(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  function writeSessionItem(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (_error) {
      // Session storage may be unavailable; cart sync may repeat once per page load.
    }
  }

  function shouldSyncCart(attributes, forceSync) {
    if (forceSync) return true;
    if (!Object.keys(attributes).length) return false;

    const nextHash = hashAttributes(attributes);
    const previousHash = readSessionItem(SESSION_KEYS.cartHash);
    const synced = readSessionItem(SESSION_KEYS.cartSynced) === "1";

    if (!synced) return true;
    return previousHash !== nextHash;
  }

  function getOrCreateVisitSessionKey() {
    const existing = readSessionItem(SESSION_KEYS.visitSessionKey);
    if (typeof existing === "string" && existing.length > 0) {
      return existing;
    }

    const sessionKey = Date.now().toString(36) + Math.random().toString(36).slice(2);
    writeSessionItem(SESSION_KEYS.visitSessionKey, sessionKey);
    return sessionKey;
  }

  function buildVisitPayload(latestTouch) {
    if (!latestTouch || typeof latestTouch !== "object") {
      return null;
    }

    const sessionKey = getOrCreateVisitSessionKey();
    const payload = {
      session_key: sessionKey,
    };

    const fields = [
      "ref",
      "bdm_asset",
      "bdm_channel",
      "bdm_campaign",
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "landing_page",
      "landing_path",
      "referrer",
    ];

    fields.forEach((field) => {
      const value = latestTouch[field];
      if (typeof value === "string" && value) {
        payload[field] = value;
      }
    });

    return payload;
  }

  function hashVisitPayload(payload) {
    const normalized = {};
    VISIT_HASH_FIELDS.forEach((field) => {
      normalized[field] = payload[field] || "";
    });
    return JSON.stringify(normalized);
  }

  function sendVisitBeacon(payload) {
    const body = JSON.stringify(payload);

    try {
      if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
        const blob = new Blob([body], { type: "application/json" });
        if (navigator.sendBeacon(VISIT_ENDPOINT, blob)) {
          return;
        }
      }
    } catch (_error) {
      // Fall through to fetch keepalive.
    }

    if (!window.fetch) {
      return;
    }

    window
      .fetch(VISIT_ENDPOINT, {
        method: "POST",
        mode: "cors",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
        },
        body,
      })
      .catch((error) => {
        warn("Could not send partner visit beacon.", error);
      });
  }

  function maybeTrackVisit(latestTouch, hasMeaningfulTrackingParams) {
    if (!hasMeaningfulTrackingParams) {
      return;
    }

    const payload = buildVisitPayload(latestTouch);
    if (!payload) {
      return;
    }

    const nextHash = hashVisitPayload(payload);
    const previousHash = readSessionItem(SESSION_KEYS.visitHash);
    if (previousHash === nextHash) {
      return;
    }

    sendVisitBeacon(payload);
    writeSessionItem(SESSION_KEYS.visitHash, nextHash);
  }

  function syncCartAttributes(attributes, forceSync) {
    if (!window.fetch || !shouldSyncCart(attributes, forceSync)) {
      return;
    }

    const payloadHash = hashAttributes(attributes);

    window
      .fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ attributes }),
        credentials: "same-origin",
      })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Cart update failed with status ${response.status}`);
        }
        writeSessionItem(SESSION_KEYS.cartHash, payloadHash);
        writeSessionItem(SESSION_KEYS.cartSynced, "1");
      })
      .catch((error) => {
        warn("Could not sync partner attribution to cart.", error);
      });
  }

  function run() {
    if (!isTopWindow()) return;

    const searchParams = readSearchParams();
    const hasTrackingInUrl = hasMeaningfulTrackingParams(searchParams);

    let firstTouch = readStoredAttribution(STORAGE_KEYS.first);
    let latestTouch = readStoredAttribution(STORAGE_KEYS.latest);

    if (hasTrackingInUrl) {
      const incoming = buildAttributionSnapshot(searchParams);

      if (!firstTouch) {
        firstTouch = { ...incoming };
        persistAttribution(STORAGE_KEYS.first, firstTouch);
      }

      latestTouch = {
        ...incoming,
        first_seen_at: latestTouch?.first_seen_at || incoming.first_seen_at,
        last_seen_at: incoming.last_seen_at,
      };
      persistAttribution(STORAGE_KEYS.latest, latestTouch);

      const attributes = buildCartAttributes(firstTouch, latestTouch);
      syncCartAttributes(attributes, true);
      maybeTrackVisit(latestTouch, true);
      return;
    }

    if (!firstTouch && !latestTouch) {
      return;
    }

    const attributes = buildCartAttributes(firstTouch, latestTouch);
    syncCartAttributes(attributes, false);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run, { once: true });
  } else {
    run();
  }
})();
